/**
 * Recognition page — list recognition badges and give new recognition.
 *
 * Any authenticated user can give recognition. The `public` flag controls
 * whether other employees see it; private recognition is only visible to the
 * recipient and managers/admins (enforced server-side).
 * Admins can edit/delete any; givers can edit their own.
 */
import { useMemo, useState } from 'react';
import { Star, Plus, Globe, Lock, Pencil, Trash2, FileDown } from 'lucide-react';
import {
  useRecognition,
  useCreateRecognition,
  useUpdateRecognition,
  useDeleteRecognition,
} from '../api.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Textarea, Field } from '../../../shell/Field.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import { RECOGNITION_BADGE, type RecognitionBadge } from '@shared/enums/index.js';
import type { Recognition } from '@shared/types';

export function RecognitionPage() {
  const { data, isLoading } = useRecognition();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Recognition | null>(null);
  const deleteRecognition = useDeleteRecognition();

  const handleDelete = async (id: string) => {
    try {
      await deleteRecognition.mutateAsync(id);
      toast('success', 'Recognition deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete recognition.';
      toast('error', msg);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Recognition</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{data?.items.length ?? 0} badges given</p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" /> Give Recognition
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((r) => {
            const canEdit = user?.role === 'Admin' || r.givenBy === user?.id;
            const canDelete = canEdit;
            return (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between gap-2">
                  <Badge tone="purple">{r.badge}</Badge>
                  <div className="flex items-center gap-1">
                    {r.public ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                        <Globe className="h-3 w-3" /> Public
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                        <Lock className="h-3 w-3" /> Private
                      </span>
                    )}
                    <a
                      href={`/api/action-pdfs/recognition/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button size="sm" variant="ghost" aria-label="Download PDF">
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </a>
                    {canEdit && (
                      <Button size="sm" variant="ghost" aria-label="Edit recognition" onClick={() => setEditTarget(r)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Delete recognition"
                        loading={deleteRecognition.isPending}
                        onClick={() => handleDelete(r.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {employeeLabel.get(r.employeeId) ?? 'Employee'}
                </p>
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{r.text}</p>
                <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">{formatDate(r.date)}</p>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<Star className="h-8 w-8" />}
          title="No recognition yet"
          description="Recognize a colleague's great work."
        />
      )}

      <GiveRecognitionModal open={modalOpen} onClose={() => setModalOpen(false)} />
      {editTarget && (
        <EditRecognitionModal
          recognition={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

function GiveRecognitionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateRecognition();
  const today = new Date().toISOString().slice(0, 10);
  const [employeeId, setEmployeeId] = useState('');
  const [badge, setBadge] = useState<RecognitionBadge>('Teamwork');
  const [text, setText] = useState('');
  const [date, setDate] = useState(today);
  const [isPublic, setIsPublic] = useState(true);

  const reset = () => {
    setEmployeeId('');
    setBadge('Teamwork');
    setText('');
    setDate(today);
    setIsPublic(true);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await create.mutateAsync({
        employeeId,
        badge,
        text,
        date: new Date(`${date}T00:00:00.000Z`).toISOString(),
        public: isPublic,
      });
      toast('success', 'Recognition given.');
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to give recognition.';
      toast('error', msg);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Give Recognition">
      <div className="space-y-4">
        <EmployeeSelect
          label="Employee"
          value={employeeId}
          onChange={setEmployeeId}
          directory
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Badge"
            value={badge}
            onChange={(e) => setBadge(e.target.value as RecognitionBadge)}
          >
            {RECOGNITION_BADGE.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </Select>
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <Textarea
          label="Message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Great job on…"
        />
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
          />
          Make this recognition public
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={create.isPending}
            disabled={!employeeId || !text.trim()}
          >
            Give
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditRecognitionModal({
  recognition,
  onClose,
}: {
  recognition: Recognition;
  onClose: () => void;
}) {
  const update = useUpdateRecognition();
  const [badge, setBadge] = useState<RecognitionBadge>(recognition.badge);
  const [text, setText] = useState(recognition.text);
  const [date, setDate] = useState(recognition.date.slice(0, 10));
  const [isPublic, setIsPublic] = useState(recognition.public);

  const handleSubmit = async () => {
    try {
      await update.mutateAsync({
        id: recognition.id,
        body: {
          badge,
          text,
          date: new Date(`${date}T00:00:00.000Z`).toISOString(),
          public: isPublic,
        },
      });
      toast('success', 'Recognition updated.');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to update recognition.';
      toast('error', msg);
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit Recognition">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Badge"
            value={badge}
            onChange={(e) => setBadge(e.target.value as RecognitionBadge)}
          >
            {RECOGNITION_BADGE.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </Select>
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <Textarea
          label="Message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
          />
          Make this recognition public
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={update.isPending} disabled={!text.trim()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
